import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Grid, TextField, Typography, Link, FormControlLabel, Checkbox } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS } from 'date-fns/locale';
import type { PortalEventPayload } from '../../portal/portalDraftEnvelope';
import {
  buildEventDatetimeFields,
  endOfDay,
  mergeDatePart,
  mergeTimePart,
  parseEventStartEnd,
  startOfDay,
} from '../../utils/eventOdDo';
import { DraftHeroBanner } from './DraftHeroBanner';
import { ArticleBodyEditorJs } from './ArticleBodyEditorJs';

const COLUMN_MIN_HEIGHT = 420;

const openPickerButtonSx = {
  color: '#d4d4d8',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
};

const outlinedInputSx = {
  color: '#fafafa',
  '& fieldset': { borderColor: '#3f3f46' },
  '&:hover fieldset': { borderColor: '#a1a1aa' },
  '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': outlinedInputSx,
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
};

export type EventDraftPanelProps = {
  notebookId: string;
  event: PortalEventPayload;
  saveEvent: (next: PortalEventPayload) => void;
  sourceClustersSlot?: React.ReactNode;
  draftAccessBar?: React.ReactNode;
  accessInHeader?: boolean;
  readOnly?: boolean;
};

export function EventDraftPanel({
  notebookId,
  event,
  saveEvent,
  sourceClustersSlot,
  draftAccessBar,
  accessInHeader = false,
  readOnly = false,
}: EventDraftPanelProps) {
  const eventRef = useRef(event);
  eventRef.current = event;
  const [startAt, setStartAt] = useState<Date>(() => parseEventStartEnd(event).start);
  const [endAt, setEndAt] = useState<Date>(() => parseEventStartEnd(event).end);
  useEffect(() => {
    const { start, end } = parseEventStartEnd(event);
    setStartAt(start);
    setEndAt(end);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional narrow deps
  }, [event.trajanje_start, event.trajanje_end, event.all_day]);

  const pushSchedule = useCallback(
    (start: Date, end: Date, opts?: { allDay?: boolean }) => {
      const allDay = opts?.allDay !== undefined ? opts.allDay : Boolean(event.all_day);
      let s = new Date(start);
      let e = new Date(end);
      if (allDay) {
        s = startOfDay(s);
        e = endOfDay(e);
        if (e.getTime() < s.getTime()) {
          e = endOfDay(s);
        }
      } else if (e.getTime() <= s.getTime()) {
        e = new Date(s.getTime() + 15 * 60 * 1000);
      }
      const dt = buildEventDatetimeFields(notebookId, s, e, event.dedup_key, { allDay });
      saveEvent({ ...event, ...dt, all_day: allDay, url: event.url || '' });
    },
    [event, notebookId, saveEvent]
  );

  const allDay = Boolean(event.all_day);
  const sForLabel = allDay ? startOfDay(startAt) : startAt;
  const eForLabel = allDay ? endOfDay(endAt) : endAt;
  const computedTrajanje = buildEventDatetimeFields(
    notebookId,
    sForLabel,
    eForLabel,
    event.dedup_key,
    { allDay }
  ).trajanje;

  const locationQuery = (event.location || '').trim();
  const embedKey = process.env.REACT_APP_GOOGLE_MAPS_EMBED_API_KEY?.trim();
  const mapSrc = locationQuery
    ? embedKey
      ? `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(embedKey)}&q=${encodeURIComponent(locationQuery)}`
      : `https://www.google.com/maps?q=${encodeURIComponent(locationQuery)}&z=16&output=embed`
    : '';

  const dateFormat = 'MMM d, yyyy';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
      {!accessInHeader ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fafafa' }}>
            Event draft
          </Typography>
          {draftAccessBar ? (
            <Box sx={{ flex: 1, minWidth: 200, maxWidth: 560, textAlign: 'right' }}>{draftAccessBar}</Box>
          ) : null}
        </Box>
      ) : null}
      <DraftHeroBanner
        imageUrl={event.og_image || ''}
        onImageUrlCommitted={(url) => saveEvent({ ...event, og_image: url, url: event.url || '' })}
        ariaLabel="Event hero image"
        maxHeight={200}
        required
        readOnly={readOnly}
      />
      <Box component="fieldset" disabled={readOnly} sx={{ border: 'none', m: 0, p: 0, minWidth: 0 }}>
      {sourceClustersSlot ? <Box sx={{ mb: 2 }}>{sourceClustersSlot}</Box> : null}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!accessInHeader ? (
            <TextField
              fullWidth
              required
              label="Title"
              value={event.title}
              onChange={(e) => saveEvent({ ...event, title: e.target.value, url: event.url || '' })}
              sx={{ ...fieldSx, mb: 2 }}
            />
          ) : null}

          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ color: '#a1a1aa' }}>
              Start — end <Typography component="span" sx={{ color: '#f87171' }}>*</Typography>
            </Typography>
            <FormControlLabel
              sx={{ ml: 'auto', mr: 0, color: '#a1a1aa' }}
              control={
                <Checkbox
                  checked={allDay}
                  onChange={(_, checked) => {
                    if (checked) {
                      let s = startOfDay(startAt);
                      let e = endOfDay(endAt);
                      if (e.getTime() < s.getTime()) {
                        e = endOfDay(s);
                      }
                      setStartAt(s);
                      setEndAt(e);
                      pushSchedule(s, e, { allDay: true });
                    } else {
                      const base = startOfDay(startAt);
                      const s = new Date(base);
                      s.setHours(9, 0, 0, 0);
                      const e = new Date(s.getTime() + 60 * 60 * 1000);
                      setStartAt(s);
                      setEndAt(e);
                      pushSchedule(s, e, { allDay: false });
                    }
                  }}
                  sx={{ color: '#a1a1aa', '&.Mui-checked': { color: '#60a5fa' } }}
                />
              }
              label="All day"
            />
            <FormControlLabel
              sx={{ mr: 0, color: '#a1a1aa' }}
              control={
                <Checkbox
                  checked={Boolean(event.check_in_enabled)}
                  onChange={(_, checked) => {
                    saveEvent({ ...event, check_in_enabled: checked, url: event.url || '' });
                  }}
                  sx={{ color: '#a1a1aa', '&.Mui-checked': { color: '#22c55e' } }}
                />
              }
              label="QR check-in"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'flex-start',
              gap: 0.75,
              mb: 2,
              overflowX: 'auto',
              overflowY: 'visible',
              pt: 0.5,
              pb: 1,
              width: '100%',
            }}
          >
            <DesktopDatePicker
              format={dateFormat}
              label="Start date"
              value={startAt}
              onChange={(v) => {
                const next = mergeDatePart(startAt, v);
                setStartAt(next);
                pushSchedule(next, endAt);
              }}
              slotProps={{
                openPickerButton: { sx: openPickerButtonSx },
                textField: {
                  size: 'small',
                  required: true,
                  InputLabelProps: { shrink: true },
                  inputProps: { 'aria-label': 'Start date' },
                  sx: {
                    minWidth: 124,
                    '& .MuiOutlinedInput-root': outlinedInputSx,
                    '& .MuiInputLabel-root': { color: '#a1a1aa' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
                  },
                },
              }}
            />
            {!allDay && (
              <DesktopTimePicker
                label="Start time"
                value={startAt}
                onChange={(v) => {
                  const next = mergeTimePart(startAt, v);
                  setStartAt(next);
                  pushSchedule(next, endAt);
                }}
                slotProps={{
                  openPickerButton: { sx: openPickerButtonSx },
                  textField: {
                    size: 'small',
                    required: true,
                    InputLabelProps: { shrink: true },
                    sx: {
                      minWidth: 104,
                      '& .MuiOutlinedInput-root': outlinedInputSx,
                      '& .MuiInputLabel-root': { color: '#a1a1aa' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
                    },
                  },
                }}
              />
            )}
            <Typography sx={{ color: '#71717a', px: 0.25, flexShrink: 0, alignSelf: 'center', mt: 1.25 }}>
              to
            </Typography>
            {!allDay && (
              <DesktopTimePicker
                label="End time"
                value={endAt}
                onChange={(v) => {
                  const next = mergeTimePart(endAt, v);
                  setEndAt(next);
                  pushSchedule(startAt, next);
                }}
                slotProps={{
                  openPickerButton: { sx: openPickerButtonSx },
                  textField: {
                    size: 'small',
                    required: true,
                    InputLabelProps: { shrink: true },
                    sx: {
                      minWidth: 104,
                      '& .MuiOutlinedInput-root': outlinedInputSx,
                      '& .MuiInputLabel-root': { color: '#a1a1aa' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
                    },
                  },
                }}
              />
            )}
            <DesktopDatePicker
              format={dateFormat}
              label="End date"
              value={endAt}
              onChange={(v) => {
                const next = mergeDatePart(endAt, v);
                setEndAt(next);
                pushSchedule(startAt, next);
              }}
              slotProps={{
                openPickerButton: { sx: openPickerButtonSx },
                textField: {
                  size: 'small',
                  required: true,
                  InputLabelProps: { shrink: true },
                  inputProps: { 'aria-label': 'End date' },
                  sx: {
                    minWidth: 124,
                    '& .MuiOutlinedInput-root': outlinedInputSx,
                    '& .MuiInputLabel-root': { color: '#a1a1aa' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
                  },
                },
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
            Duration:{' '}
            <Box component="span" sx={{ color: '#fafafa', fontWeight: 600 }}>
              {computedTrajanje}
            </Box>
          </Typography>

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={6} sx={{ display: 'flex', minHeight: COLUMN_MIN_HEIGHT }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: COLUMN_MIN_HEIGHT, width: '100%' }}>
                <Typography variant="caption" sx={{ color: '#a1a1aa', mb: 0.5 }}>
                  Description
                </Typography>
                <ArticleBodyEditorJs
                  notebookId={notebookId}
                  readOnly={readOnly}
                  seed={{ content: '', content_blocks: event.content_blocks || [] }}
                  onBlurValidation={() => {}}
                  onBodyChange={(_content, content_blocks) => {
                    const current = eventRef.current;
                    saveEvent({ ...current, url: current.url || '', content_blocks });
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', minHeight: COLUMN_MIN_HEIGHT }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  minHeight: COLUMN_MIN_HEIGHT,
                  flex: 1,
                }}
              >
                <TextField
                  fullWidth
                  required
                  label="Location"
                  value={event.location || ''}
                  onChange={(e) => saveEvent({ ...event, location: e.target.value, url: event.url || '' })}
                  sx={{ ...fieldSx, mb: 1, flexShrink: 0 }}
                  InputProps={{
                    startAdornment: (
                      <LocationOnIcon sx={{ color: '#71717a', mr: 1 }} fontSize="small" />
                    ),
                  }}
                />
                <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mb: 1, flexShrink: 0 }}>
                  Check on the map.{' '}
                  <Link
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationQuery)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#60a5fa' }}
                  >
                    Google Maps
                  </Link>
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid #27272a',
                    bgcolor: '#171717',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {mapSrc ? (
                    <Box
                      component="iframe"
                      title="Map preview"
                      src={mapSrc}
                      sx={{ flex: 1, width: '100%', minHeight: 200, border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#52525b',
                        px: 2,
                        textAlign: 'center',
                      }}
                    >
                      Enter a location to preview the map.
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      </Box>
    </LocalizationProvider>
  );
}
